import React, { createContext, useEffect, useState, useCallback, ReactNode } from "react";
import { onAuthStateChanged, signInAnonymously, User, linkWithCredential, EmailAuthProvider, sendEmailVerification, GoogleAuthProvider, linkWithPopup } from "firebase/auth";
import { doc, getDoc, setDoc, Timestamp, deleteDoc } from 'firebase/firestore';
import { auth, db } from "../../services/firebase/firebaseConfig";
import { UserInitializationService, UserProfile } from "../../services/MMR";
import { AnonymousAccountManager, AccountType, LoginState } from "../../services/auth/AnonymousAccountManager";
import { UserDataService } from "../../services/firebase/userDataService";
import { containsInappropriateContent, getCleanAlternative } from "../../utils/profanityFilter";
import { MMRService } from "../../services/MMR/MMRService";
import { migrateUserToTimeControlRatings, reconcileLegacyFields } from "../../services/MMR/migration";
import { detectUserRegion } from "../../utils/regionDetection";
import { isAge16OrOlder } from "../../utils/ageUtils";

interface AuthContextValue {
  user: User | null;
  userProfile: UserProfile | null;
  profileLoading: boolean;
  authInitializing: boolean;
  accountType: AccountType;
  canPromote: boolean;
  isPremium: boolean;
  loginState: LoginState | null;
  linkWithEmail: (email: string, password: string, dateOfBirth?: string) => Promise<{ success: boolean; error?: string }>;
  linkWithGoogle: () => Promise<{ success: boolean; error?: string }>;
  updateProfile: (profile: UserProfile) => Promise<void>;
  updateZodiacResult: (zodiacResult: string) => Promise<{ success: boolean; error?: string }>;
  changeUsername: (newUsername: string) => Promise<{ success: boolean; error?: string }>;
  canChangeUsername: () => { canChange: boolean; reason?: string };
}

export const AuthContext = createContext<AuthContextValue>({
  user: null,
  userProfile: null,
  profileLoading: false,
  authInitializing: true,
  accountType: 'new',
  canPromote: false,
  isPremium: false,
  loginState: null,
  linkWithEmail: async () => ({ success: false, error: 'Not implemented' }),
  linkWithGoogle: async () => ({ success: false, error: 'Not implemented' }),
  updateProfile: async () => {},
  updateZodiacResult: async () => ({ success: false, error: 'Not implemented' }),
  changeUsername: async () => ({ success: false, error: 'Not implemented' }),
  canChangeUsername: () => ({ canChange: false, reason: 'Not implemented' }),
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [accountType, setAccountType] = useState<AccountType>('new');
  const [canPromote, setCanPromote] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [loginState, setLoginState] = useState<LoginState | null>(null);

  useEffect(() => {
    const initializeAuth = async () => {

      // First, check localStorage for existing login state
      const existingLoginState = AnonymousAccountManager.getLoginState();
      setLoginState(existingLoginState);
      setAccountType(existingLoginState.accountType);


      // CRITICAL: Wait for Firebase auth to initialize before making decisions

      // Wait for Firebase to finish loading auth state
      await new Promise<void>((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          unsubscribe();
          resolve();
        });
      });

      // Handle different account types

      switch (existingLoginState.accountType) {
        case 'new':
          await handleNewUser();
          break;

        case 'temp':
          await handleTempUser(existingLoginState);
          break;

        case 'real':
          await handleRealUser(existingLoginState);
          break;
      }

      setInitializing(false);
    };

    initializeAuth();
  }, []);

  const handleNewUser = async () => {

    // Sign in to Firebase anonymously first
    try {
      const result = await signInAnonymously(auth);


      setUser(result.user);

      // Create temporary account data
      const tempAccount = await AnonymousAccountManager.createTempAccount();


      setUserProfile(tempAccount.profile);
      setAccountType('temp');
      setCanPromote(false);

      // CRITICAL: Store user data in Firebase immediately
      await UserDataService.createAnonymousUserData(
        result.user.uid,
        tempAccount.profile,
        tempAccount.deviceId
      );

      // Update temp account with Firebase UID
      const updatedAccount = { ...tempAccount, firebaseUid: result.user.uid };
      localStorage.setItem('zodiac_temp_account', JSON.stringify(updatedAccount));


    } catch (error) {
    }
  };

  const handleTempUser = async (loginState: LoginState) => {

    const tempAccount = AnonymousAccountManager.getTempAccount();
    if (!tempAccount) {
      await handleNewUser();
      return;
    }


    // Check if account is older than 1 year of inactivity
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    if (tempAccount.lastActive < oneYearAgo) {
      AnonymousAccountManager.resetAll();
      await handleNewUser();
      return;
    }

    // If we have a Firebase UID, try to restore Firebase session and get latest data
    if (tempAccount.firebaseUid) {

      // Set profile from localStorage immediately (fast initial load)
      setUserProfile(tempAccount.profile);
      setCanPromote(calculateCanPromote(tempAccount.profile));

      // Then try to restore Firebase session and sync with server data
      try {
        // Check if Firebase session exists
        const currentUser = auth.currentUser;

        if (currentUser && currentUser.uid === tempAccount.firebaseUid) {
          setUser(currentUser);

          // Load latest data from Firebase
          const firebaseUserData = await UserDataService.getUserData(currentUser.uid);

          if (firebaseUserData) {

            // Run migration if needed
            const migratedProfile = await migrateUserToTimeControlRatings(currentUser.uid, firebaseUserData.profile);

            // Reconcile stale legacy fields (fixes accounts where TC ratings are ahead of legacy)
            const reconciledProfile = await reconcileLegacyFields(currentUser.uid, migratedProfile);

            setUserProfile(reconciledProfile);
            setCanPromote(calculateCanPromote(migratedProfile));

            // Auto-detect region if UNKNOWN for temp users
            if (migratedProfile.region === 'UNKNOWN' || !migratedProfile.region) {
              const regionInfo = detectUserRegion();
              if (regionInfo.region !== 'UNKNOWN') {
                migratedProfile.region = regionInfo.region;
                migratedProfile.regionDetectedBy = regionInfo.detectionMethod;
                setUserProfile({ ...migratedProfile });
                // Update Firebase
                try {
                  await UserDataService.updateUserProfile(currentUser.uid, migratedProfile);
                } catch (regionError) {
                }
              }
            }

            // Update localStorage cache
            const updatedTempAccount = { ...tempAccount, profile: migratedProfile };
            localStorage.setItem('zodiac_temp_account', JSON.stringify(updatedTempAccount));
          }
        } else {
          // Firebase session lost, sign in anonymously again

          const result = await signInAnonymously(auth);
          setUser(result.user);

        }
      } catch (error) {
        // Already set from localStorage above, so just continue
      }
    } else {

      const result = await signInAnonymously(auth);
      setUser(result.user);

      // Migrate localStorage data to Firebase
      await UserDataService.createAnonymousUserData(
        result.user.uid,
        tempAccount.profile,
        tempAccount.deviceId
      );

      // Update localStorage with Firebase UID
      const updatedAccount = { ...tempAccount, firebaseUid: result.user.uid };
      localStorage.setItem('zodiac_temp_account', JSON.stringify(updatedAccount));

      setUserProfile(tempAccount.profile);
      setCanPromote(calculateCanPromote(tempAccount.profile));

      // Auto-detect region if UNKNOWN for migrated temp account
      if (tempAccount.profile.region === 'UNKNOWN' || !tempAccount.profile.region) {
        const regionInfo = detectUserRegion();
        if (regionInfo.region !== 'UNKNOWN') {
          tempAccount.profile.region = regionInfo.region;
          tempAccount.profile.regionDetectedBy = regionInfo.detectionMethod;
          setUserProfile({ ...tempAccount.profile });
          // Update Firebase
          try {
            await UserDataService.updateUserProfile(result.user.uid, tempAccount.profile);
          } catch (regionError) {
          }
        }
      }
    }

  };

  const handleRealUser = async (loginState: LoginState) => {

    if (loginState.lastLoginEmail) {
    } else {
    }

    // Check if Firebase already has an active authenticated session
    const currentUser = auth.currentUser;

    if (currentUser && !currentUser.isAnonymous) {

      // Set the user immediately
      setUser(currentUser);
      setAccountType('real');

      // The onAuthStateChanged listener will handle loading the profile
      return;
    }

    // No active session, so create anonymous account
    await handleNewUser();
  };

  // Listen for Firebase auth state changes (separate from initialization)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {

      if (currentUser) {
      } else {
      }

      // Handle real user authentication (when user signs in from temp account)
      if (currentUser && !currentUser.isAnonymous) {
        setUser(currentUser);

        // Update account type if transitioning from temp
        if (accountType !== 'real') {
          setAccountType('real');

          // Update login state in localStorage
          if (currentUser.email) {
            AnonymousAccountManager.rememberRealAccountLogin(currentUser.email);
          }
        }

        // Load real user profile from Firebase
        setProfileLoading(true);
        try {
          const profile = await UserInitializationService.ensureUserProfile(currentUser);
          setUserProfile(profile);


          // Auto-detect region if UNKNOWN
          if (profile.region === 'UNKNOWN' || !profile.region) {
            const regionInfo = detectUserRegion();
            if (regionInfo.region !== 'UNKNOWN') {
              profile.region = regionInfo.region;
              profile.regionDetectedBy = regionInfo.detectionMethod;
              // Update profile in Firebase
              try {
                await UserDataService.updateUserProfile(currentUser.uid, profile);
                setUserProfile({ ...profile }); // Update local state
              } catch (regionError) {
              }
            } else {
            }
          }

        } catch (error) {
          setUserProfile(null);
        } finally {
          setProfileLoading(false);
        }
      } else if (currentUser && currentUser.isAnonymous) {
      }

    });

    return () => unsubscribe();
  }, [accountType]);

  // Recalculate canPromote whenever userProfile changes (Firebase-first)
  useEffect(() => {
    if (userProfile && accountType === 'temp') {
      const shouldPromote = calculateCanPromote(userProfile);
      setCanPromote(shouldPromote);
    } else {
      setCanPromote(false);
    }
  }, [userProfile, accountType]);

  // Update isPremium whenever userProfile changes
  useEffect(() => {
    if (userProfile) {
      setIsPremium(userProfile.isPremium === true && accountType === 'real');
    } else {
      setIsPremium(false);
    }
  }, [userProfile, accountType]);

  // Account linking function - CRITICAL improvement
  const linkWithEmail = async (email: string, password: string, dateOfBirth?: string): Promise<{ success: boolean; error?: string }> => {

    if (!user || !user.isAnonymous) {
      return { success: false, error: 'No anonymous user to link' };
    }


    try {
      const credential = EmailAuthProvider.credential(email, password);

      const result = await linkWithCredential(user, credential);

      // Send email verification
      try {
        await sendEmailVerification(result.user);
      } catch (emailError) {
        // Don't fail the whole operation if email sending fails
      }

      // Upgrade account in Firebase
      await UserDataService.upgradeToAuthenticatedAccount(result.user.uid, email);

      // Update profile with DOB and chatEnabled if DOB was provided
      if (dateOfBirth && userProfile) {
        const chatEnabled = isAge16OrOlder(dateOfBirth);
        const updatedProfile = {
          ...userProfile,
          dateOfBirth,
          chatEnabled
        };
        await UserDataService.updateUserProfile(result.user.uid, updatedProfile);
        setUserProfile(updatedProfile);
      }

      // Update local state
      setAccountType('real');
      setUser(result.user);

      // Update login state
      AnonymousAccountManager.rememberRealAccountLogin(email);

      // Clear temp account data
      AnonymousAccountManager.clearTempAccount();

      return { success: true };

    } catch (error: any) {

      // Handle specific Firebase auth errors with comprehensive coverage
      switch (error.code) {
        case 'auth/email-already-in-use':
          return { success: false, error: 'This email is already registered. Please sign in instead.' };

        case 'auth/weak-password':
          return { success: false, error: 'Password is too weak. Please choose a stronger password (at least 6 characters).' };

        case 'auth/invalid-email':
          return { success: false, error: 'Invalid email address. Please check and try again.' };

        case 'auth/provider-already-linked':
          return { success: false, error: 'This account is already linked to an email. Please sign in instead.' };

        case 'auth/credential-already-in-use':
          return { success: false, error: 'These credentials are already in use by another account. Please sign in or use different credentials.' };

        case 'auth/requires-recent-login':
          return { success: false, error: 'For security, please sign out and sign in again before linking your account.' };

        case 'auth/operation-not-allowed':
          return { success: false, error: 'Email/password accounts are not enabled. Please contact support.' };

        case 'auth/invalid-credential':
          return { success: false, error: 'Invalid credentials. Please check your email and password.' };

        case 'auth/network-request-failed':
          return { success: false, error: 'Network error. Please check your connection and try again.' };

        case 'auth/too-many-requests':
          return { success: false, error: 'Too many attempts. Please wait a moment and try again.' };

        default:
          return { success: false, error: `Failed to create account: ${error.message || 'Unknown error'}` };
      }
    }
  };

  // Google account linking function
  const linkWithGoogle = async (): Promise<{ success: boolean; error?: string }> => {

    if (!user || !user.isAnonymous) {
      return { success: false, error: 'No anonymous user to link' };
    }


    try {
      const provider = new GoogleAuthProvider();

      const result = await linkWithPopup(user, provider);

      const email = result.user.email || '';

      // Upgrade account in Firebase
      await UserDataService.upgradeToAuthenticatedAccount(result.user.uid, email);

      // Update local state
      setAccountType('real');
      setUser(result.user);

      // Update login state
      AnonymousAccountManager.rememberRealAccountLogin(email);

      // Clear temp account data
      AnonymousAccountManager.clearTempAccount();

      return { success: true };

    } catch (error: any) {

      // Handle specific Firebase auth errors
      switch (error.code) {
        case 'auth/credential-already-in-use':
          return { success: false, error: 'This Google account is already in use by another account. Please sign in with that account instead.' };

        case 'auth/provider-already-linked':
          return { success: false, error: 'This account is already linked to a Google account.' };

        case 'auth/popup-closed-by-user':
          return { success: false, error: 'Sign-in popup was closed. Please try again.' };

        case 'auth/popup-blocked':
          return { success: false, error: 'Popup was blocked by your browser. Please allow popups and try again.' };

        case 'auth/cancelled-popup-request':
          return { success: false, error: 'Sign-in was cancelled. Please try again.' };

        case 'auth/network-request-failed':
          return { success: false, error: 'Network error. Please check your connection and try again.' };

        case 'auth/too-many-requests':
          return { success: false, error: 'Too many attempts. Please wait a moment and try again.' };

        default:
          return { success: false, error: `Failed to link Google account: ${error.message || 'Unknown error'}` };
      }
    }
  };

  // Profile update function that syncs to Firebase
  const updateProfile = async (profile: UserProfile): Promise<void> => {

    if (!user) {
      throw new Error('No user logged in');
    }

    try {
      // Update Firebase first (server-side truth)
      await UserDataService.updateUserProfile(user.uid, profile);

      // Update local state
      setUserProfile(profile);

      // Update localStorage for temp accounts (with Firebase UID)
      if (accountType === 'temp') {
        await AnonymousAccountManager.updateTempAccount(profile, user.uid);
      }

    } catch (error) {
      throw error;
    }
  };

  // Update zodiac result function
  const updateZodiacResult = useCallback(async (zodiacResult: string): Promise<{ success: boolean; error?: string }> => {

    if (!user || !userProfile) {
      return { success: false, error: 'User not authenticated' };
    }

    try {
      const updatedProfile: UserProfile = {
        ...userProfile,
        zodiacResult: zodiacResult,
        lastAvatarChange: new Date(), // Track when avatar was changed
      };

      await updateProfile(updatedProfile);
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to save zodiac result. Please try again.' };
    }
  }, [user, userProfile, updateProfile]);

  // Username change function with restrictions for real accounts
  const changeUsername = async (newUsername: string): Promise<{ success: boolean; error?: string }> => {
    if (!user || !userProfile) {
      return { success: false, error: 'User not authenticated' };
    }

    // For temp accounts, use AnonymousAccountManager
    if (accountType === 'temp') {
      return await AnonymousAccountManager.changeTempAccountUsername(newUsername);
    }

    try {
      // Allow free changes during placement (first 10 games)
      // After placement, enforce 90-day cooldown
      if (userProfile.mmr.totalGames >= 10 && userProfile.lastUsernameChange) {
        const daysSinceLastChange = Math.floor(
          (Date.now() - userProfile.lastUsernameChange.getTime()) / (1000 * 60 * 60 * 24)
        );
        if (daysSinceLastChange < 90) {
          const daysRemaining = 90 - daysSinceLastChange;
          return { success: false, error: `You can change your username in ${daysRemaining} days` };
        }
      }

      // Clean and validate new username
      const cleanUsername = cleanUsernameInput(newUsername);
      if (!cleanUsername) {
        return { success: false, error: 'Username must be 3-12 characters, letters and numbers only' };
      }

      // Professional profanity and inappropriate content check
      if (containsInappropriateContent(cleanUsername)) {
        return { success: false, error: 'Username contains inappropriate language' };
      }

      // Check if new username is available
      const usernamesRef = doc(db, 'usernames', cleanUsername);
      const usernameDoc = await getDoc(usernamesRef);

      if (usernameDoc.exists()) {
        return { success: false, error: 'Username is already taken' };
      }

      // Reserve new username and release old one
      await setDoc(usernamesRef, { taken: true, createdAt: Timestamp.now() });
      if (userProfile.username) {
        await deleteDoc(doc(db, 'usernames', userProfile.username));
      }

      // Update user profile
      const updatedProfile: UserProfile = {
        ...userProfile,
        username: cleanUsername,
        lastUsernameChange: new Date(),
        usernameChangeCount: (userProfile.usernameChangeCount || 0) + 1,
      };

      await updateProfile(updatedProfile);
      return { success: true };

    } catch (error) {
      return { success: false, error: 'Failed to change username. Please try again.' };
    }
  };

  // Check if user can change username
  const canChangeUsername = (): { canChange: boolean; reason?: string } => {
    if (!userProfile) return { canChange: false, reason: 'User not loaded' };

    // For temp accounts, use AnonymousAccountManager
    if (accountType === 'temp') {
      return AnonymousAccountManager.canChangeTempUsername();
    }

    // Allow free changes during placement (first 10 games)
    if (userProfile.mmr.totalGames < 10) {
      return { canChange: true, reason: 'Free changes during placement' };
    }

    // After placement, check 90-day cooldown
    if (userProfile.lastUsernameChange) {
      const daysSinceLastChange = Math.floor(
        (Date.now() - userProfile.lastUsernameChange.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (daysSinceLastChange < 90) {
        const daysRemaining = 90 - daysSinceLastChange;
        return { canChange: false, reason: `Available in ${daysRemaining} days` };
      }
    }

    return { canChange: true };
  };

  // Render children immediately - profile will load progressively
  return (
    <AuthContext.Provider value={{
      user,
      userProfile,
      profileLoading,
      authInitializing: initializing,
      accountType,
      canPromote,
      isPremium,
      loginState,
      linkWithEmail,
      linkWithGoogle,
      updateProfile,
      updateZodiacResult,
      changeUsername,
      canChangeUsername
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Helper function to calculate promotion eligibility from Firebase data ONLY
// This ensures we're not dependent on localStorage for critical decisions
const calculateCanPromote = (profile: UserProfile): boolean => {
  const currentRating = MMRService.getHighestRating(profile).rating;

  return (
    profile.mmr.totalGames >= 3 ||
    profile.mmr.placementGames >= 7 ||
    currentRating >= 1400
  );
};

// Helper function for username validation
const cleanUsernameInput = (name: string): string | null => {
  const cleaned = name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .substring(0, 12);

  if (cleaned.length < 3) return null;
  return cleaned;
};
